import { IDropdownOption } from '../../proservContractTypes';

class DropdownOptionsRepository {
    private options = new Map<string, Promise<IDropdownOption[]>>();

    private callbackFunctions = new Map<string, [() => void]>();

    private deferredResolvers = new Map<string, (options: IDropdownOption[] | Promise<IDropdownOption[]>) => void>();

    setDropdownOptions(fieldKey: string, options: Promise<IDropdownOption[]> | IDropdownOption[]): void {
        this.resolveDeferredIfNeeded(fieldKey, options);
        this.options.set(fieldKey, Promise.resolve(options));
        const callbackFunctions = this.callbackFunctions.get(fieldKey);
        if (callbackFunctions) {
            callbackFunctions.forEach((callbackFunction) => callbackFunction?.());
        }
    }

    /**
     * Register a callback function to be called when the dropdown options are set.
     * This is useful for cases where the options are fetched asynchronously when a document view model param changes, and we want to inform the dropdown components that use that data
     *
     * @param fieldKey - The key of the field for which the options are being set.
     * @param callbackFunction - The function to be called when the options are set.
     *
     **/
    registerCallback(fieldKey: string, callbackFunction: () => void): void {
        if (this.callbackFunctions.has(fieldKey)) {
            const callbackFunctions = this.callbackFunctions.get(fieldKey)!;
            callbackFunctions.push(callbackFunction);
            return;
        }
        this.callbackFunctions.set(fieldKey, [callbackFunction]);
    }

    getLatestDropdownOptions(fieldKey: string): Promise<IDropdownOption[]> {
        this.createDeferredIfNeeded(fieldKey);
        return this.options.get(fieldKey)!;
    }

    /**
     * The options may not have started being fetched when the validation function executes.
     *
     * In this case, we store a promise for the options as well as a function to resolve the promise later.
     *
     * When the options are set later on, we will resolve this promise if we find that we have stored a function
     * to resolve it.
     *
     * Doing this will allow "suspending" the validation function until the options become available.
     */
    private createDeferredIfNeeded(fieldKey: string) {
        if (!this.options.get(fieldKey)) {
            this.options.set(
                fieldKey,
                new Promise((res) => {
                    this.deferredResolvers.set(fieldKey, res);
                })
            );
        }
    }

    /**
     * See `createDeferredIfNeeded`. Here is where we resolve the promises it creates.
     */
    private resolveDeferredIfNeeded(fieldKey: string, options: Promise<IDropdownOption[]> | IDropdownOption[]) {
        const res = this.deferredResolvers.get(fieldKey);
        if (!res) {
            return;
        }

        // no need to do this again
        this.deferredResolvers.delete(fieldKey);

        res(options);
    }
}

export default DropdownOptionsRepository;
